import { Buffer } from 'buffer';

import { type DocNode } from '@atlaskit/adf-schema';
import {
  ProviderFactory,
  type Providers,
} from '@atlaskit/editor-common/provider-factory';
import { type EventHandlers } from '@atlaskit/editor-common/ui';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { ReactRenderer, type RendererProps } from '@atlaskit/renderer';
import React, { useMemo } from 'react';
import type { MutableRefObject } from 'react';
import { IntlProvider } from 'react-intl-next';
import { useSnapshot } from 'valtio';

import { type ADFDocument, type TextAlignment } from '@canvas/adf';
import { currentSiteData } from '@canvas/canvas-state/site-data';
import { useUserLocale } from '@canvas/canvas-state/use-user-locale';
import { currentUserState } from '@canvas/canvas-state/user';
import { useMessageString } from '@canvas/intl';
import { profileCardClient } from '@canvas/profile-card-utils/profile-card-client';
import { getViewProfileUrl, getGiveKudosUrl } from '@canvas/profile-card-utils/utils';
import { smartLinkClient } from '@canvas/smart-link-client';
import { testId } from '@canvas/testing/test-id';
import { type Unpromise } from '@canvas/utils';

import styles from '../editor.module.css';
import { messages } from '../i18n';
import { cardProvider } from '../providers/card-provider';
import { emojiProvider } from '../providers/emoji-provider';
import { mediaProps } from '../providers/media-provider';
import { getMentionProvider } from '../providers/mention-provider';

import { getCommentFontScale } from './comment-editor-helpers';

window.Buffer = Buffer; // @atlaskit/media-viewer needs this polyfill to work

interface CommentRendererProps {
  document: ADFDocument | undefined;
  alignment: TextAlignment;
  cloudId?: string;
}

/**
 * Navigate to the given URL in the topmost Confluence frame. This avoids only the
 * whiteboard iframe from being navigated.
 */
const linkEventHandler = (
  event: React.SyntheticEvent<HTMLAnchorElement, Event | MouseEvent>,
  url?: string
) => {
  // eslint-disable-next-line no-restricted-syntax
  const topFrame = window.top;
  if (url && topFrame) {
    const isUrlSameOrigin = new URL(url).origin === topFrame.location.origin;
    const userOpeningInNewTab =
      ('ctrlKey' in event.nativeEvent && event.nativeEvent.ctrlKey) ||
      ('shiftKey' in event.nativeEvent && event.nativeEvent.shiftKey) ||
      ('metaKey' in event.nativeEvent && event.nativeEvent.metaKey);

    if (!isUrlSameOrigin || userOpeningInNewTab) {
      // Open in new tab if the URL is not same origin or user is opening in new tab
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      // Otherwise, navigate in the top frame
      topFrame.location.href = url;
    }

    event.preventDefault();
  }
};

const rendererEventHandlers: EventHandlers = {
  link: {
    onClick: linkEventHandler,
  },
  smartCard: {
    onClick: linkEventHandler,
  },
};

const smartLinkOptions: RendererProps['smartLinks'] = {
  hideHoverPreview: true,
};

function getRendererRoot(
  editorRef: MutableRefObject<HTMLDivElement | null>
): HTMLElement | null {
  return (
    editorRef.current?.querySelector<HTMLElement>(
      `.ak-renderer-wrapper .ak-renderer-document`
    ) ?? null
  );
}

export type EditorProfileCardProvider = Unpromise<Providers['profilecardProvider']>;

export const CommentRenderer: React.FC<CommentRendererProps> = ({
  document,
  alignment,
  cloudId,
}) => {
  const rendererRef = React.useRef<HTMLDivElement | null>(null);

  const site = useSnapshot(currentSiteData);
  const accountId = useSnapshot(currentUserState)?.identity?.accountId;
  const locale = useUserLocale();

  const viewProfile = useMessageString({
    message: messages['whiteboards.editor.comments.view-profile'],
  });

  const giveKudos = useMessageString({
    message: messages['whiteboards.editor.comments.give-kudos'],
  });

  const profileCardProvider = useMemo(
    () =>
      Promise.resolve({
        cloudId: site.siteId ?? '',
        resourceClient: profileCardClient,
        getActions: (profileAccountId: string) => {
          const actions = [
            {
              label: viewProfile,
              callback: () =>
                /** We always want to open in a new tab. */
                window.open(
                  getViewProfileUrl(profileAccountId),
                  '_blank',
                  'noopener,noreferrer'
                ),
            },
            {
              label: giveKudos,
              callback: () =>
                window.open(
                  getGiveKudosUrl(site.siteId, profileAccountId),
                  '_blank',
                  'noopener,noreferrer'
                ),
            },
          ];

          return actions;
        },
      } satisfies EditorProfileCardProvider),
    [giveKudos, site.siteId, viewProfile]
  );

  const dataProviders = ProviderFactory.create({
    emojiProvider,
    cardProvider,
    mentionProvider: getMentionProvider(accountId, cloudId),
    profilecardProvider: profileCardProvider,
    mediaProvider: mediaProps.provider,
  });

  React.useEffect(() => {
    const rendererRoot = getRendererRoot(rendererRef);
    const fontScale = getCommentFontScale();

    if (rendererRoot) {
      rendererRoot.style.setProperty('--canvas-editor-font-scale', `${fontScale}`);
      rendererRoot.style.setProperty('--canvas-editor-alignment', alignment);
    }
  });

  return (
    <div
      ref={rendererRef}
      className={styles.CommentRenderer}
      data-testid={testId('comment-renderer')}
      data-is-text-selectable
    >
      <IntlProvider locale={locale}>
        <SmartCardProvider client={smartLinkClient}>
          <ReactRenderer
            document={document as DocNode}
            dataProviders={dataProviders}
            smartLinks={smartLinkOptions}
            eventHandlers={rendererEventHandlers}
            shouldOpenMediaViewer={false}
          />
        </SmartCardProvider>
      </IntlProvider>
    </div>
  );
};
